import { watchEffect } from "vue";
import api from "@/api";
import router from "@/router";
import { axios } from "@/services";
import {
	cookies,
	authToken,
	saveToken,
	authStatus,
	isLoggedIn,
	updateAuthStatus,
	activateTokenRefreshTimer,
} from "@/state/api/authState";
import { updateUserInfos, resetUserInfos } from "@/state/api/userState";

import { detectPlatform, validToken, getExpDiff } from "@/utils";

const platform = detectPlatform();

const getUserInfos = async () => {
	// We're retrieving the user's info
	await api()
		.user.getUserInfos()
		.then(async (userInfos: any) => {
			// Save user infos
			updateUserInfos(userInfos.data);

			// Hydrate the feature-flags store (cascade Enterprise→Station resolved server-side).
			// Failure is non-fatal — the app falls back to default-enabled features.
			try {
				const r: any = await api().featureFlags.me();
				const { setFeatures } = await import("@/state/api/featuresState");
				setFeatures(r?.data?.features || {});
			} catch (e) { console.warn("[features] hydration failed", e); }

			// S'abonner au broadcast Reverb des toggles features pour que
			// le sidebar / router se synchronisent sans rechargement quand
			// Root désactive un module pour cette entreprise.
			try {
				const enterpriseId = userInfos.data?.account?.enterprise_id
					?? userInfos.data?.organisation?.id
					?? null;
				const echo = await import("@/services/featuresEcho");
				if (enterpriseId) {
					echo.subscribeFeatures(enterpriseId);
				}
				// Filet de sécurité : si Reverb est down (dev sans WS,
				// pare-feu, route mal config) ou si l'admin avait sa tab
				// inactive au moment du toggle Root, on rate l'event live.
				// Le auto-refresh re-tire /features/me dès que la tab
				// redevient visible — l'Admin n'a plus besoin de se
				// reconnecter pour voir les modules nouvellement activés.
				echo.installFeaturesAutoRefresh();

				// Palier 3 — canal fleet : un changement de statut/départ
				// d'embarquement invalide la query Vue Query → les listes se
				// rafraîchissent en temps réel sur tous les terminaux.
				const fleet = await import("@/services/fleetEcho");
				if (enterpriseId) fleet.subscribeFleet(enterpriseId);
			} catch (e) { console.warn("[features-echo] subscribe failed", e); }

			// Change auth status to signal user connected
			updateAuthStatus("connected");
		})
		.catch((err: any) => {
			console.error("%c Warning ", "color: #FFC400;", "Auto login Error :", err?.data?.response?.message || err?.data?.message || err?.message || err);
			updateAuthStatus("error");
			if (err?.data?.data?.code) {
				redirectToMaintenancePage(err);
			}
		});
};

const getNewToken = (token: any) => {
	// Change auth status to token is refreshing
	updateAuthStatus("refreshing");
	// Launch API call
	api()
		.auth.refreshToken({ refresh_token: token.key_res })
		.then((response: any) => {
			updateAuthStatus("refreshed");

			saveToken(response).then(async () => {
				// We get the user's information
				await getUserInfos();
			});
		})
		.catch((err: any) => {
			router.push({
				name: "auth-logout",
				params: { action: "force" },
			});
		});
};

const loginWithToken = async () => {
	// detect curent route
	const currentRoute = window.location.href.split("auth/").pop();
	// Check if it is different from logout
	if (currentRoute != "logout" && currentRoute != "logout/force") {
		const token = validToken();

		// Change auth status to loading
		updateAuthStatus("loading");

		// Execute actions according to the token status
		switch (token.status) {
			case "valid":
				// Authenticate with token
				axios.defaults.headers.common.Authorization = `Bearer ${token.key}`;
				activateTokenRefreshTimer(token);

				// We get the user's information
				await getUserInfos();
				break;

			case "invalid":
				// Set auth to initial status
				updateAuthStatus("initial");
				break;

			case "empty":
				// Set auth to initial status
				updateAuthStatus("initial");
				break;

			default:
				break;
		}
	}
};

const redirectToMaintenancePage = (error:any) => {
	if (error?.data?.data?.code === "0x0041") {
		router.push({
			name: "auth-blocked",
			params: { mode: "forbidden" },
			query: {
				message: error.data.data.signature,
			},
		});
	} else if (error?.data?.data?.code === "0x0042") {
		router.push({
			name: "auth-blocked",
			params: { mode: "maintenance" },
			query: {
				message: error.data.data.signature,
			},
		});
	}
}


export default { loginWithToken, redirectToMaintenancePage };
